import React, { useState } from 'react';
import { Modal } from 'antd';
import { util } from '../../../config/util';
import './SubscriptionDetailModal.scss';

interface SingleSubscriptionInfo {
  productId: number;
  productName: string;
  amount: number;
  isYearly: boolean;
  periodText: string;
  nextPaymentDate: string;
  startDate: string;
  isActive: boolean;
  billingKey?: string;
  scheduleId?: string;
  cancel_requested?: boolean;
  expiry_date?: string | null;
}


interface SubscriptionDetailModalProps {
  visible: boolean;
  subscription: SingleSubscriptionInfo | null;
  onClose: () => void;
  onCancel: (productId: number, reason?: string, detail?: string) => void;
  cardInfo?: {
    cardBrand?: string;
    cardLast4?: string;
  };
  hasPromotion?: boolean;
  promotionInfo?: {
    promotion_type: string | null;
    remaining_days: number;
  };
  allActivePromotions?: {  // 🎁 모든 활성 프로모션 정보
    promotion_type: string;
    korean_name: string;
    remaining_days: number;
    end_date: string | null;
    start_date: string | null;
    lost_on_cancel: boolean;
  }[];
}

const SubscriptionDetailModal: React.FC<SubscriptionDetailModalProps> = ({
  visible,
  subscription,
  onClose,
  onCancel,
  cardInfo,
  hasPromotion = false,
  promotionInfo,
  allActivePromotions = []
}) => {
  
  const [showPromotionWarning, setShowPromotionWarning] = useState(false);
  const [showCancelForm, setShowCancelForm] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelDetail, setCancelDetail] = useState('');

  // 날짜 포맷팅 함수
  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // 카드 브랜드명 정리 함수
  const getCleanCardBrand = (cardBrand: string | undefined) => {
    if (!cardBrand) return '카드';
    
    // PG사명 제거하고 실제 카드사명만 추출
    let cleanBrand = cardBrand
      .replace(/INICIS_V2/gi, '')
      .replace(/NICE_V2/gi, '')
      .replace(/KCP_V2/gi, '')
      .replace(/TOSS_V2/gi, '')
      .replace(/_V2/gi, '')
      .replace(/INICIS/gi, '')
      .replace(/NICE/gi, '')
      .replace(/KCP/gi, '')
      .replace(/TOSS/gi, '')
      .replace(/PAYPAL/gi, '')
      .trim();
    
    // 빈 문자열이거나 알 수 없는 경우 기본값
    if (!cleanBrand || cleanBrand.length === 0) {
      return '카드';
    }
    
    // 카드사명 매핑 (대소문자 무시)
    const cardBrandMap: { [key: string]: string } = {
      'VISA': '비자카드',
      'MASTER': '마스터카드', 
      'MASTERCARD': '마스터카드',
      'SAMSUNG': '삼성카드',
      'HYUNDAI': '현대카드',
      'LOTTE': '롯데카드',
      'KB': 'KB국민카드',
      'KOOKMIN': 'KB국민카드',
      'SHINHAN': '신한카드',
      'WOORI': '우리카드',
      'HANA': '하나카드',
      'BC': 'BC카드',
      'NH': 'NH농협카드',
      'NONGHYUP': 'NH농협카드',
      'CITI': '씨티카드'
    };
    
    const upperBrand = cleanBrand.toUpperCase();
    for (const [key, value] of Object.entries(cardBrandMap)) {
      if (upperBrand.includes(key)) {
        return value;
      }
    }
    
    return cleanBrand;
  };

  // 프로모션 타입에 따른 한글 이름 반환
  const getPromotionTypeName = (type: string | null) => {
    switch (type) {
      case 'trial_3days':
        return '3일 무료체험';
      case 'first_subscription_2weeks':
        return '첫 구독 2주 무료체험';  // 🎁 더 명확한 표현
      case '첫 구독 2주 무료체험':  // 🎁 백엔드에서 한글로 보낼 경우
        return '첫 구독 2주 무료체험';
      default:
        return '프로모션';
    }
  };

  // 해지하기 버튼 클릭 - 1단계: 프로모션 경고
  const handleCancelClick = () => {
    console.log("🎁 [구독 취소] 해지 버튼 클릭");
    console.log("🎁 [구독 취소] 구독 정보:", subscription);
    console.log("🎁 [구독 취소] hasPromotion:", hasPromotion);
    console.log("🎁 [구독 취소] promotionInfo:", promotionInfo);
    console.log("🎁 [구독 취소] allActivePromotions:", allActivePromotions);
    
    if (!subscription) {
      console.log("❌ [구독 취소] 구독 정보 없음");
      return;
    }
    
    if (hasPromotion && promotionInfo) {
      console.log("🚨 [구독 취소] 프로모션 손실 경고 표시");
      console.log("   - 프로모션 타입:", promotionInfo.promotion_type);
      console.log("   - 남은 기간:", promotionInfo.remaining_days, "일");
      console.log("   - 프로모션 이름:", getPromotionTypeName(promotionInfo.promotion_type));
      setShowPromotionWarning(true);
    } else {
      console.log("✅ [구독 취소] 프로모션 손실 없음 - 취소 폼으로 바로 이동");
      setShowCancelForm(true);
    }
  };

  // 프로모션 경고 후 계속 진행
  const handleProceedAfterWarning = () => {
    setShowPromotionWarning(false);
    setShowCancelForm(true);
  };

  // 해지 사유 제출
  const handleSubmitCancellation = () => {
    if (!subscription || !cancelReason) {
      alert('해지 사유를 선택해주세요.');
      return;
    }
    
    onCancel(subscription.productId, cancelReason, cancelDetail);
    setShowCancelForm(false);
    onClose();
  };

  // 모달 초기화
  const resetModal = () => {
    setShowPromotionWarning(false);
    setShowCancelForm(false);
    setCancelReason('');
    setCancelDetail('');
  };

  // 모달 닫기
  const handleModalClose = () => {
    resetModal();
    onClose();
  };


  if (!subscription) return null;

  return (
    <>
      {/* 메인 구독 상세 모달 */}
      {visible && !showPromotionWarning && !showCancelForm && (
        <Modal open={visible} onClose={handleModalClose} centered>
          <div className="modal-header border-bottom">
            <div className="d-flex align-items-center">
              <img 
                src="https://sulfac-image.s3.ap-northeast-2.amazonaws.com/None_1721128376471805.png" 
                alt="구독" 
                className="me-3"
                style={{ width: '32px', height: '32px', borderRadius: '6px' }}
              />
              <div>
                <h4 className="mb-0">{subscription.productName}</h4>
                <small className="text-muted">구독 상세 정보</small>
              </div>
            </div>
            <button className="btn-close" onClick={handleModalClose} aria-label="Close"></button>
          </div>

          <div className="modal-body p-4">
            {/* 패키지 정보 섹션 */}
            <div className="mb-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="section-title mb-0">
                  패키지 정보
                </h5>
                
                {/* 해지하기 버튼 - 이미 해지 예정이면 숨김 */}
                {!subscription.cancel_requested && (
                  <button
                    type="button"
                    className="btn btn-link text-muted text-decoration-none p-0"
                    onClick={handleCancelClick}
                    style={{ fontSize: '0.875rem' }}
                  >
                    구독 해지
                  </button>
                )}
              </div>
              
              <div className="row g-3">
                <div className="col-sm-6">
                  <div className="card h-100 border-0 bg-light">
                    <div className="card-body p-3">
                      <div className="d-flex align-items-center">
                        <small className="text-muted mb-1">결제 금액</small>
                      </div>
                      <div className="fw-bold fs-5 text-success">
                        {util.addComma(subscription.amount)}원
                        <small className="text-muted fw-normal">/ {subscription.periodText}</small>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-sm-6">
                  <div className="card h-100 border-0 bg-light">
                    <div className="card-body p-3">
                      <div className="d-flex align-items-center">
                        <small className="text-muted mb-1">구독 상태</small>
                      </div>
                      {subscription.cancel_requested ? (
                        <span className="badge fs-6 bg-warning text-dark">종료 예정</span>
                      ) : (
                        <span className="badge fs-6 bg-success">구독 중</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="col-sm-6">
                  <div className="card h-100 border-0 bg-light">
                    <div className="card-body p-3">
                      <div className="d-flex align-items-center">
                        <small className="text-muted mb-1">구독 시작일</small>
                      </div>
                      <div className="fw-bold">{formatDate(subscription.startDate)}</div>
                    </div>
                  </div>
                </div>
                <div className="col-sm-6">
                  <div className="card h-100 border-0 bg-light">
                    <div className="card-body p-3">
                      <div className="d-flex align-items-center">
                        <small className="text-muted mb-1">{subscription.cancel_requested ? '서비스 종료일' : '다음 결제일'}</small>
                      </div>
                      <div className="fw-bold">{formatDate(subscription.cancel_requested && subscription.expiry_date ? subscription.expiry_date : subscription.nextPaymentDate)}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 결제수단 정보 섹션 */}
            {cardInfo && (
              <div className="mb-4">
                <h5 className="section-title mb-3">
                  결제수단
                </h5>
                <div className="card border">
                  <div className="card-body">
                    <div className="d-flex align-items-center justify-content-between">
                      <div className="d-flex align-items-center">
                        <div>
                          <div className="fw-bold mb-1">
                            {getCleanCardBrand(cardInfo.cardBrand)} **** **** **** {cardInfo.cardLast4 || '1234'}
                          </div>
                          <small className="text-muted">
                            자동결제 등록됨
                          </small>
                        </div>
                      </div>
                      <span className="badge bg-success">활성</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* 🎁 활성 프로모션 정보 섹션 */}
            {allActivePromotions && allActivePromotions.length > 0 && (
              <div className="mb-4">
                <h5 className="section-title mb-3">
                  활성 프로모션
                </h5>
                <div className="row g-3">
                  {allActivePromotions.map((promotion, index) => (
                    <div key={index} className="col-12">
                      <div className="card border">
                        <div className="card-body">
                          <div className="d-flex align-items-center justify-content-between">
                            <div>
                              <div className="fw-bold mb-1 text-primary">
                                {promotion.korean_name}
                              </div>
                              <div className="d-flex align-items-center gap-3">
                                <small className="text-muted">
                                  남은 기간: <strong>{promotion.remaining_days}일</strong>
                                </small>
                                {promotion.end_date && (
                                  <small className="text-muted">
                                    종료일: {formatDate(promotion.end_date)}
                                  </small>
                                )}
                              </div>
                            </div>
                            <div className="d-flex align-items-center gap-2">
                              <span className="badge bg-success">활성</span>
                              {promotion.lost_on_cancel && (
                                <span className="badge bg-warning text-dark">구독 중단시 소멸</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

        </Modal>
      )}

      {/* 프로모션 경고 모달 */}
      {showPromotionWarning && (
        <Modal open={true} onClose={() => setShowPromotionWarning(false)} centered>
          <div className="modal-header border-bottom">
            <h5 className="modal-title">
              프로모션 혜택 손실 안내
            </h5>
            <button className="btn-close" onClick={() => setShowPromotionWarning(false)} aria-label="Close"></button>
          </div>
          
          <div className="modal-body p-4">
            <div className="alert alert-warning" role="alert">
              <div>
                <h6 className="alert-heading mb-2">현재 적용 중인 프로모션</h6>
                <p className="mb-2">
                  <strong>{getPromotionTypeName(promotionInfo?.promotion_type || null)}</strong> 혜택을 받고 계십니다.
                </p>
                <p className="mb-0 text-muted small">
                  남은 기간: <strong>{promotionInfo?.remaining_days || 0}일</strong>
                </p>
              </div>
            </div>
            
            <div className="mt-4">
              <h6 className="mb-3">
                해지 시 주의사항
              </h6>
              <ul className="list-unstyled mb-0">
                <li className="mb-2">
                  • 프로모션 혜택이 즉시 종료됩니다
                </li>
                <li className="mb-2">
                  • 남은 무료 기간을 사용할 수 없습니다
                </li>
                <li className="mb-0">
                  • 동일한 프로모션을 다시 받을 수 없습니다
                </li>
              </ul>
            </div>
          </div>
          
          <div className="modal-footer border-top">
            <button 
              type="button" 
              className="btn btn-secondary me-2" 
              onClick={() => setShowPromotionWarning(false)}
            >
돌아가기
            </button>
            <button 
              type="button" 
              className="btn btn-danger" 
              onClick={handleProceedAfterWarning}
            >
그래도 해지하기
            </button>
          </div>
        </Modal>
      )}

      {/* 해지 사유 선택 모달 */}
      {showCancelForm && (
        <Modal open={true} onClose={() => setShowCancelForm(false)} centered>
          <div className="modal-header border-bottom">
            <h5 className="modal-title">
              구독 해지 사유
            </h5>
            <button className="btn-close" onClick={() => setShowCancelForm(false)} aria-label="Close"></button>
          </div>
          
          <div className="modal-body p-4">
            <div className="mb-4">
              <p className="text-muted mb-3">
                더 나은 서비스를 위해 해지 사유를 알려주세요
              </p>
            </div>
            
            <div className="mb-4">
              <label className="form-label fw-bold">
                해지 사유 선택 *
              </label>
              <select 
                className="form-select" 
                value={cancelReason} 
                onChange={(e) => setCancelReason(e.target.value)}
                required
              >
                <option value="">해지 사유를 선택해주세요</option>
                <option value="price_burden">💰 가격이 부담스러워요</option>
                <option value="service_dissatisfaction">😞 서비스가 만족스럽지 않아요</option>
                <option value="low_usage">📊 사용 빈도가 낮아요</option>
                <option value="other_service">🔄 다른 서비스를 이용할 예정이에요</option>
                <option value="feature_lack">⚙️ 필요한 기능이 부족해요</option>
                <option value="difficult_usage">🤔 사용법이 어려워요</option>
                <option value="support_dissatisfaction">📞 고객지원에 불만이 있어요</option>
                <option value="other">🔧 기타</option>
              </select>
            </div>
            
            <div className="mb-4">
              <label className="form-label fw-bold">
                상세 사유 (선택사항)
              </label>
              <textarea
                className="form-control"
                rows={4}
                placeholder="추가로 알려주고 싶은 내용이 있다면 자유롭게 작성해주세요. (최대 500자)"
                value={cancelDetail}
                onChange={(e) => setCancelDetail(e.target.value)}
                maxLength={500}
              ></textarea>
              <div className="form-text text-end">
                {cancelDetail.length}/500자
              </div>
            </div>
          </div>
          
          <div className="modal-footer border-top">
            <button 
              type="button" 
              className="btn btn-secondary me-2" 
              onClick={() => setShowCancelForm(false)}
            >
취소
            </button>
            <button 
              type="button" 
              className="btn btn-danger" 
              onClick={handleSubmitCancellation}
              disabled={!cancelReason}
            >
해지 완료
            </button>
          </div>
        </Modal>
      )}
    </>
  );
};

export default SubscriptionDetailModal;